import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

const fmt = (n) =>
  typeof n === 'number' ? n.toLocaleString(undefined, {maximumFractionDigits: 2}) : '—'

const WorkStatusTaskTable = ({tasks}) => {
  const intl = useIntl()

  const totals = useMemo(
    () =>
      tasks.reduce(
        (acc, t) => ({
          totalWork: acc.totalWork + (t.totalWork ?? 0),
          completedWork: acc.completedWork + (t.completedWork ?? 0),
          remainingWork: acc.remainingWork + (t.remainingWork ?? 0),
        }),
        {totalWork: 0, completedWork: 0, remainingWork: 0}
      ),
    [tasks]
  )

  const taskStatusBadge = (rate) => {
    if (rate >= 100)
      return (
        <span className='badge badge-light-success'>
          {intl.formatMessage({id: 'WORKSTATUS.TASK.STATUS.COMPLETED'})}
        </span>
      )
    if (rate > 0)
      return (
        <span className='badge badge-light-primary'>
          {intl.formatMessage({id: 'WORKSTATUS.TASK.STATUS.INPROGRESS'})}
        </span>
      )
    return (
      <span className='badge badge-light-secondary'>
        {intl.formatMessage({id: 'WORKSTATUS.TASK.STATUS.NOTSTARTED'})}
      </span>
    )
  }

  if (!tasks.length) {
    return (
      <div className='alert alert-info'>
        {intl.formatMessage({id: 'WORKSTATUS.TABLE.EMPTY'})}
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>
            {intl.formatMessage({id: 'WORKSTATUS.TABLE.TITLE'})}
          </span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            {intl.formatMessage({id: 'WORKSTATUS.TABLE.SUBTITLE'}, {count: tasks.length})}
          </span>
        </h3>
      </div>
      <div className='card-body py-3'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted'>
                <th>{intl.formatMessage({id: 'WORKSTATUS.TABLE.COL.NAME'})}</th>
                <th className='text-end'>{intl.formatMessage({id: 'WORKSTATUS.TABLE.COL.TOTALWORK'})}</th>
                <th className='text-end'>{intl.formatMessage({id: 'WORKSTATUS.TABLE.COL.RATE'})}</th>
                <th className='text-end'>{intl.formatMessage({id: 'WORKSTATUS.TABLE.COL.COMPLETED'})}</th>
                <th className='text-end'>{intl.formatMessage({id: 'WORKSTATUS.TABLE.COL.REMAINING'})}</th>
                <th className='text-nowrap'>{intl.formatMessage({id: 'WORKSTATUS.TABLE.COL.STARTDATE'})}</th>
                <th className='text-nowrap'>{intl.formatMessage({id: 'WORKSTATUS.TABLE.COL.ENDDATE'})}</th>
                <th className='text-nowrap'>{intl.formatMessage({id: 'WORKSTATUS.TABLE.COL.STATUS'})}</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id}>
                  <td>
                    <span className='text-dark fw-bold text-hover-primary fs-6'>{task.name}</span>
                  </td>
                  <td className='text-end fw-semibold'>{fmt(task.totalWork)}</td>
                  <td className='text-end'>
                    <div className='d-flex align-items-center justify-content-end'>
                      <span className='me-2 fw-semibold'>{fmt(task.completionRate)}%</span>
                      <div className='progress h-6px w-75px' style={{minWidth: 60}}>
                        <div
                          className='progress-bar bg-primary'
                          role='progressbar'
                          style={{width: `${Math.min(task.completionRate, 100)}%`}}
                        />
                      </div>
                    </div>
                  </td>
                  <td className='text-end fw-semibold text-success'>{fmt(task.completedWork)}</td>
                  <td className='text-end fw-semibold text-danger'>{fmt(task.remainingWork)}</td>
                  <td className='text-muted text-nowrap'>
                    {task.startDate
                      ? intl.formatDate(new Date(task.startDate), {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className='text-muted text-nowrap'>
                    {task.endDate
                      ? intl.formatDate(new Date(task.endDate), {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className='text-nowrap'>
                    {task.taskStateName ? (
                      <span className='badge badge-light-primary'>{task.taskStateName}</span>
                    ) : (
                      taskStatusBadge(task.completionRate)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className='fw-bold fs-6 border-top border-gray-300'>
                <td>{intl.formatMessage({id: 'WORKSTATUS.TABLE.FOOTER.TOTAL'})}</td>
                <td className='text-end'>{fmt(totals.totalWork)}</td>
                <td />
                <td className='text-end text-success'>{fmt(totals.completedWork)}</td>
                <td className='text-end text-danger'>{fmt(totals.remainingWork)}</td>
                <td />
                <td />
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  )
}

WorkStatusTaskTable.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      totalWork: PropTypes.number,
      completionRate: PropTypes.number,
      completedWork: PropTypes.number,
      remainingWork: PropTypes.number,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      taskStateName: PropTypes.string,
    })
  ).isRequired,
}

export {WorkStatusTaskTable}
